import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { Field, TextInput, Select, PrimaryButton, ResultStat } from "@/tools/ui";
import { API } from "@/lib/api";
import axios from "axios";

const GenericConverter = ({ units, testid }) => {
  const keys = Object.keys(units);
  const [from, setFrom] = useState(keys[0]);
  const [to, setTo] = useState(keys[1]);
  const [val, setVal] = useState("1");

  const base = parseFloat(val) * units[from];
  const out = isNaN(base) ? "" : (base / units[to]).toLocaleString(undefined, { maximumFractionDigits: 6 });

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div data-testid={testid} className="space-y-6">
      <Field label="Value"><TextInput data-testid={`${testid}-value`} type="number" value={val} onChange={(e) => setVal(e.target.value)} /></Field>
      <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-[1fr_auto_1fr]">
        <Field label="From">
          <Select data-testid={`${testid}-from`} value={from} onChange={(e) => setFrom(e.target.value)}>
            {keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </Select>
        </Field>
        <button data-testid={`${testid}-swap`} onClick={swap} className="mb-1 flex h-10 w-10 items-center justify-center border border-border bg-card transition-colors hover:bg-primary hover:text-white">
          <ArrowRightLeft className="h-4 w-4" />
        </button>
        <Field label="To">
          <Select data-testid={`${testid}-to`} value={to} onChange={(e) => setTo(e.target.value)}>
            {keys.map((k) => <option key={k} value={k}>{k}</option>)}
          </Select>
        </Field>
      </div>
      <div className="border border-primary bg-primary p-6 text-white">
        <p className="overline text-blue-200">Result</p>
        <p data-testid={`${testid}-result`} className="mt-1 font-heading text-3xl font-black tracking-tight break-all">{out} <span className="text-lg font-medium text-blue-200">{to}</span></p>
      </div>
    </div>
  );
};

export const LengthConverter = () => (
  <GenericConverter testid="length" units={{ mm: 0.001, cm: 0.01, m: 1, km: 1000, in: 0.0254, ft: 0.3048, yd: 0.9144, mi: 1609.344 }} />
);

export const WeightConverter = () => (
  <GenericConverter testid="weight" units={{ mg: 0.000001, g: 0.001, kg: 1, ton: 1000, oz: 0.0283495, lb: 0.453592 }} />
);

export const DataStorageConverter = () => (
  <GenericConverter testid="data-storage" units={{ B: 1, KB: 1024, MB: 1048576, GB: 1073741824, TB: 1099511627776 }} />
);

export const AreaConverter = () => (
  <GenericConverter testid="area" units={{ "mm²": 0.000001, "cm²": 0.0001, "m²": 1, "km²": 1000000, "hectare": 10000, "acre": 4046.86, "ft²": 0.092903, "yd²": 0.836127, "mi²": 2589988 }} />
);

export const SpeedConverter = () => (
  <GenericConverter testid="speed" units={{ "m/s": 1, "km/h": 0.277778, "mph": 0.44704, "knot": 0.514444, "ft/s": 0.3048 }} />
);

export const VolumeConverter = () => (
  <GenericConverter testid="volume" units={{ ml: 0.001, L: 1, "m³": 1000, tsp: 0.00492892, tbsp: 0.0147868, cup: 0.236588, pint: 0.473176, quart: 0.946353, gallon: 3.78541 }} />
);

export const TimeUnitConverter = () => (
  <GenericConverter testid="time-unit" units={{ ms: 0.001, second: 1, minute: 60, hour: 3600, day: 86400, week: 604800, month: 2629800, year: 31557600 }} />
);

export const NumberBaseConverter = () => {
  const [val, setVal] = useState("255");
  const [base, setBase] = useState("10");
  const parse = () => { try { const n = parseInt(val, parseInt(base)); return isNaN(n) ? null : n; } catch { return null; } };
  const n = parse();
  return (
    <div data-testid="number-base" className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[2fr_1fr]">
        <Field label="Value"><TextInput data-testid="numbase-value" value={val} onChange={(e) => setVal(e.target.value)} /></Field>
        <Field label="Input base"><Select data-testid="numbase-from" value={base} onChange={(e) => setBase(e.target.value)}><option value="2">Binary (2)</option><option value="8">Octal (8)</option><option value="10">Decimal (10)</option><option value="16">Hex (16)</option></Select></Field>
      </div>
      {n !== null && (
        <div data-testid="numbase-result" className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <ResultStat label="Binary" value={n.toString(2)} />
          <ResultStat label="Octal" value={n.toString(8)} />
          <ResultStat label="Decimal" value={n.toString(10)} accent />
          <ResultStat label="Hex" value={n.toString(16).toUpperCase()} />
        </div>
      )}
    </div>
  );
};

export const TemperatureConverter = () => {
  const [from, setFrom] = useState("C");
  const [to, setTo] = useState("F");
  const [val, setVal] = useState("0");

  const convert = () => {
    const v = parseFloat(val);
    if (isNaN(v)) return "";
    let c;
    if (from === "C") c = v;
    else if (from === "F") c = (v - 32) * (5 / 9);
    else c = v - 273.15;
    let out;
    if (to === "C") out = c;
    else if (to === "F") out = c * (9 / 5) + 32;
    else out = c + 273.15;
    return out.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const units = ["C", "F", "K"];
  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div data-testid="temperature" className="space-y-6">
      <Field label="Temperature"><TextInput data-testid="temperature-value" type="number" value={val} onChange={(e) => setVal(e.target.value)} /></Field>
      <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-[1fr_auto_1fr]">
        <Field label="From">
          <Select data-testid="temperature-from" value={from} onChange={(e) => setFrom(e.target.value)}>
            {units.map((u) => <option key={u} value={u}>°{u}</option>)}
          </Select>
        </Field>
        <button data-testid="temperature-swap" onClick={swap} className="mb-1 flex h-10 w-10 items-center justify-center border border-border bg-card transition-colors hover:bg-primary hover:text-white">
          <ArrowRightLeft className="h-4 w-4" />
        </button>
        <Field label="To">
          <Select data-testid="temperature-to" value={to} onChange={(e) => setTo(e.target.value)}>
            {units.map((u) => <option key={u} value={u}>°{u}</option>)}
          </Select>
        </Field>
      </div>
      <div className="border border-primary bg-primary p-6 text-white">
        <p className="overline text-blue-200">Result</p>
        <p data-testid="temperature-result" className="mt-1 font-heading text-3xl font-black tracking-tight">{convert()} <span className="text-lg font-medium text-blue-200">°{to}</span></p>
      </div>
    </div>
  );
};

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "JPY", "AUD", "CAD", "CHF", "CNY", "AED", "SGD", "ZAR", "BRL", "RUB", "KRW", "MXN"];

export const CurrencyConverter = () => {
  const [rates, setRates] = useState(null);
  const [base, setBase] = useState("USD");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("EUR");
  const [amount, setAmount] = useState("100");
  const [updated, setUpdated] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async (b) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/currency/rates`, { params: { base: b } });
      setRates(res.data.rates);
      setBase(res.data.base);
      setUpdated(res.data.updated || "");
    } catch {
      toast.error("Could not load live rates");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load("USD"); }, []);

  const convert = () => {
    if (!rates) return "";
    const amt = parseFloat(amount);
    if (isNaN(amt)) return "";
    const inBase = amt / (rates[from] || 1);
    const out = inBase * (rates[to] || 1);
    return out.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div data-testid="currency" className="space-y-6">
      <Field label="Amount"><TextInput data-testid="currency-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
      <div className="grid grid-cols-1 items-end gap-4 sm:grid-cols-[1fr_auto_1fr]">
        <Field label="From">
          <Select data-testid="currency-from" value={from} onChange={(e) => setFrom(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
        <button data-testid="currency-swap" onClick={swap} className="mb-1 flex h-10 w-10 items-center justify-center border border-border bg-card transition-colors hover:bg-primary hover:text-white">
          <ArrowRightLeft className="h-4 w-4" />
        </button>
        <Field label="To">
          <Select data-testid="currency-to" value={to} onChange={(e) => setTo(e.target.value)}>
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </Select>
        </Field>
      </div>
      <div className="border border-primary bg-primary p-6 text-white">
        <p className="overline text-blue-200">{loading ? "Loading live rates…" : "Result"}</p>
        <p data-testid="currency-result" className="mt-1 font-heading text-3xl font-black tracking-tight break-all">{convert()} <span className="text-lg font-medium text-blue-200">{to}</span></p>
        {updated && <p className="mt-2 text-xs text-blue-200">Rates: {updated}</p>}
      </div>
    </div>
  );
};
